import React from "react";
import { useTranslation } from "react-i18next";
import { Building2, ChevronDown, ChevronRight, Users, Briefcase } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { formatCurrencyCompact } from "../../lib/currency";

const LEVEL_COLORS = [
  "bg-primary/10 text-primary border-primary/40",
  "bg-emerald-950/20 text-emerald-400 border-emerald-900/40",
  "bg-amber-950/20 text-amber-400 border-amber-900/40",
  "bg-purple-950/20 text-purple-400 border-purple-900/40",
  "bg-pink-950/20 text-pink-400 border-pink-900/40",
  "bg-teal-950/20 text-teal-400 border-teal-900/40",
  "bg-orange-950/20 text-orange-400 border-orange-900/40",
  "bg-indigo-950/20 text-indigo-400 border-indigo-900/40",
];

const OrgDepartementNode = ({
  departement, level = 0,
  expandedNodes, selectedDepartementId, compact, showDetails,
  toggleExpand, onSelect,
}) => {
  const { t } = useTranslation();
  const isExpanded = expandedNodes.has(departement.id);
  const hasChildren = departement.enfants && departement.enfants.length > 0;
  const isSelected = selectedDepartementId === departement.id;
  const tauxOccupation =
    departement.effectif_max > 0 ? (departement.effectif_actuel / departement.effectif_max) * 100 : 0;

  return (
    <div className="select-none">
      <div className="relative group" style={{ marginLeft: `${level * (compact ? 12 : 16)}px` }}>
        {level > 0 && (
          <div className="absolute left-0 top-0 w-4 h-full">
            <div className="absolute left-2 top-0 w-px h-4 bg-border"></div>
            <div className="absolute left-2 top-4 w-2 h-px bg-border"></div>
          </div>
        )}

        <div
          className={`relative flex items-center ${compact ? "p-2" : "p-3"} rounded-xl border transition-all duration-200 cursor-pointer group-hover:shadow-md ${
            isSelected
              ? "bg-gradient-to-r from-primary/10 to-primary/5 border-primary shadow-sm ring-1 ring-primary/20"
              : "border-border hover:border-primary/40 hover:bg-accent/30"
          }`}
          onClick={() => onSelect(departement)}
        >
          <div className={`flex items-center ${compact ? "w-4" : "w-6"} mr-2`}>
            {hasChildren && (
              <Button
                variant="ghost"
                size="icon"
                className={`${compact ? "h-5 w-5" : "h-6 w-6"} hover:bg-primary/10 transition-colors`}
                onClick={(e) => { e.stopPropagation(); toggleExpand(departement.id); }}
              >
                {isExpanded ? (
                  <ChevronDown className={`${compact ? "h-3 w-3" : "h-4 w-4"} text-primary`} />
                ) : (
                  <ChevronRight className={`${compact ? "h-3 w-3" : "h-4 w-4"} text-muted-foreground`} />
                )}
              </Button>
            )}
          </div>

          <div className={`${compact ? "p-1.5" : "p-2"} rounded-lg mr-3 transition-colors ${
            isSelected ? "bg-primary/20" : "bg-muted/50 group-hover:bg-primary/10"
          }`}>
            <Building2 className={`${compact ? "h-3 w-3" : "h-4 w-4"} transition-colors ${
              isSelected ? "text-primary" : "text-muted-foreground"
            }`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <span className={`font-semibold truncate ${compact ? "text-sm" : "text-base"} ${
                isSelected ? "text-primary" : "text-foreground"
              }`}>
                {departement.name}
              </span>
              {!compact && showDetails && (
                <Badge variant="outline" className={`px-2 py-0.5 text-xs font-medium ${LEVEL_COLORS[level % LEVEL_COLORS.length]}`}>
                  N{level}
                </Badge>
              )}
              {departement.code && (
                <Badge variant="secondary" className="text-xs font-mono">{departement.code}</Badge>
              )}
            </div>

            {!compact && showDetails && (
              <div className="space-y-1">
                <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                  <span className="flex items-center space-x-1">
                    <Users className="h-3 w-3" />
                    <span className="font-medium">{departement.effectif_actuel || 0}</span>
                    <span>/</span>
                    <span>{departement.effectif_max || 0}</span>
                  </span>
                  {departement.budget_annuel && (
                    <span className="flex items-center space-x-1">
                      <Briefcase className="h-3 w-3" />
                      <span className="font-medium">{formatCurrencyCompact(departement.budget_annuel, "TND")}</span>
                    </span>
                  )}
                </div>
                {departement.effectif_max > 0 && (
                  <div className="flex items-center space-x-2">
                    <Progress value={tauxOccupation} className="h-1.5 flex-1" />
                    <span className="text-xs text-muted-foreground font-medium">{Math.round(tauxOccupation)}%</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {!compact && showDetails && (
            <div className="flex items-center space-x-2 ml-2">
              {departement.manager_name ? (
                <div className="flex items-center space-x-2">
                  <Avatar className="h-6 w-6 ring-1 ring-border">
                    <AvatarFallback className="text-xs bg-gradient-to-br from-primary/10 to-primary/5">
                      {departement.manager_name.split(" ").map((n) => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-muted-foreground font-medium hidden xl:inline max-w-20 truncate">
                    {departement.manager_name}
                  </span>
                </div>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground">{t("organigramme.unassigned")}</Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className={`${compact ? "mt-1" : "mt-2"} space-y-1`}>
          {departement.enfants.map((enfant) => (
            <OrgDepartementNode
              key={enfant.id}
              departement={enfant}
              level={level + 1}
              expandedNodes={expandedNodes}
              selectedDepartementId={selectedDepartementId}
              compact={compact}
              showDetails={showDetails}
              toggleExpand={toggleExpand}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default OrgDepartementNode;
